
import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Plus, X } from "lucide-react";
import { Input } from "@/components/ui/input";

interface TagsManagementProps {
  isPremium: boolean;
  isBasic?: boolean;
}

const TagsCategories: React.FC<TagsManagementProps> = ({ isPremium, isBasic }) => {
  const [newTag, setNewTag] = useState("");
  const [tags, setTags] = useState({
    serviceType: ["Sauna", "Cold Plunge", "Ice Bath"],
    format: ["Mobile", "Part of Spa", "Dedicated Venue"],
    experience: ["Forest Retreat", "Sea View", "Lakeside"],
    addons: ["Yoga", "Sound Bath", "Meditation"],
    audience: ["Couples", "Families", "Groups"]
  });

  const handleAddTag = (category: keyof typeof tags) => {
    if (newTag.trim()) {
      setTags(prev => ({
        ...prev,
        [category]: [...prev[category], newTag.trim()]
      }));
      setNewTag("");
    }
  };

  const handleRemoveTag = (category: keyof typeof tags, index: number) => {
    setTags(prev => ({
      ...prev,
      [category]: prev[category].filter((_, i) => i !== index)
    }));
  };

  const categories = [
    { key: "serviceType" as const, label: "Service Type" },
    { key: "format" as const, label: "Format" },
    { key: "experience" as const, label: "Experience" },
    { key: "addons" as const, label: "Add-ons" },
    { key: "audience" as const, label: "Audience" }
  ];

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle>Tags & Categories</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          {categories.map((category) => (
            <div key={category.key} className="space-y-2">
              <h3 className="text-sm font-medium text-gray-700">{category.label}</h3>
              <div className="flex flex-wrap gap-2">
                {tags[category.key].map((tag, index) => (
                  <Badge 
                    key={index}
                    variant="outline" 
                    className="py-1 px-3 bg-gray-50 hover:bg-gray-100 text-gray-700 border-gray-200 flex items-center gap-1"
                  >
                    {tag}
                    <button 
                      className="ml-1 hover:text-red-500 transition-colors" 
                      onClick={() => handleRemoveTag(category.key, index)}
                    >
                      <X size={14} />
                    </button>
                  </Badge>
                ))}
                
                {isPremium && (
                  <div className="flex gap-2 items-center">
                    <Input
                      placeholder={`Add ${category.label}`}
                      value={newTag}
                      onChange={(e) => setNewTag(e.target.value)}
                      className="h-8 w-40 text-sm"
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          handleAddTag(category.key);
                        }
                      }}
                    />
                    <Button 
                      size="sm" 
                      variant="outline" 
                      className="h-8 px-2"
                      onClick={() => handleAddTag(category.key)}
                    >
                      <Plus size={16} />
                    </Button>
                  </div>
                )}
              </div>
            </div>
          ))}

          {!isPremium && (
            <div className="bg-gray-50 border border-gray-200 rounded-md p-4 mt-4">
              <p className="text-sm text-gray-600">
                <span className="font-medium">Premium feature:</span> Upgrade to add and manage custom tags to improve your visibility in search results.
              </p>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default TagsCategories;
